import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { usePeopleList } from '../../hooks/usePeople';

export interface PersonPick {
  id: string;
  full_name: string;
  email?: string;
}

/**
 * Inline person picker — combobox-style. Click the trigger to open a
 * search dropdown of attendees; type to filter; click one to select.
 * Used inside forms (asset assignment, future "who is this for"
 * fields) so coordinators don't need to leave the page to assign.
 *
 * Distinct from the standalone <AssignAssetDialog/> modal — that one
 * is for one-off assign-from-list actions; this one is a field value.
 */
export function PersonPicker({
  value,
  onChange,
  placeholder = 'Pick a person...',
  ariaLabel,
  disabled,
}: {
  value: PersonPick | null;
  onChange: (next: PersonPick | null) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const list = usePeopleList(q);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    // Focus search on open
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function pick(p: PersonPick) {
    onChange(p);
    setOpen(false);
    setQ('');
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setQ('');
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={[
          'flex h-10 w-full items-center gap-2 rounded-md border border-line-strong/20 bg-surface px-3 text-left text-sm shadow-sm transition-colors',
          'hover:bg-surface-2',
          'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1',
          'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-fg-muted',
        ].join(' ')}
      >
        {value ? (
          <>
            <span className="min-w-0 flex-1 truncate">
              <span className="font-medium text-fg">{value.full_name}</span>
              {value.email ? (
                <span className="ml-2 text-xs text-fg-muted">{value.email}</span>
              ) : null}
            </span>
            {!disabled ? (
              <span
                role="button"
                tabIndex={-1}
                aria-label={`Clear ${value.full_name}`}
                onClick={clear}
                className="rounded p-1 text-fg-dim hover:bg-surface-2 hover:text-fg-muted"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </>
        ) : (
          <span className="flex-1 text-fg-dim">{placeholder}</span>
        )}
        <ChevronDown
          aria-hidden
          className={`h-4 w-4 shrink-0 text-fg-muted transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          className="animate-fade-in absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-hidden rounded-md bg-surface shadow-lg ring-1 ring-line/15"
        >
          <div className="border-b border-line/10 px-2 py-2">
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim"
              />
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name or email..."
                className="block h-9 w-full rounded-md border border-line-strong/20 bg-surface pl-8 pr-2 text-sm shadow-sm focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-strong focus-visible:ring-offset-1"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {list.isLoading ? (
              <div className="px-3 py-3 text-xs text-fg-muted">Loading…</div>
            ) : list.error ? (
              <div className="px-3 py-3 text-xs text-danger-soft-fg">
                {(list.error as Error).message}
              </div>
            ) : !list.data || list.data.length === 0 ? (
              <div className="px-3 py-3 text-xs text-fg-muted">
                {q.length === 0 ? 'Type to search.' : 'No matches.'}
              </div>
            ) : (
              <ul className="py-1">
                {list.data.slice(0, 30).map((p) => {
                  const selected = value?.id === p.id;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() =>
                          pick({ id: p.id, full_name: p.full_name, email: p.email })
                        }
                        className={[
                          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors',
                          selected
                            ? 'bg-surface-2 text-fg'
                            : 'hover:bg-surface-2',
                        ].join(' ')}
                      >
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {p.full_name}
                          </span>
                          <span className="block truncate text-[11px] text-fg-muted">
                            {p.email}
                            {p.position ? ` · ${p.position}` : ''}
                          </span>
                        </span>
                        {selected ? (
                          <span className="shrink-0 rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-fg-on-accent">
                            Picked
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
